import { useState } from 'react';
import { Caption1, Link } from '@fluentui/react-components';
import { FileViewer } from './FileViewer.js';
import { useMayReadFiles, usePersonFiles } from './FilesPanel.js';
import { LEGACY_LINK_LABEL } from './model.js';

export interface StoredFileLinkProps {
  readonly personId: string;
  /** The step's or requirement's committed file (`storedFileId`), if any. */
  readonly storedFileId: string | null;
  /** A retained pre-ADR-009 `sharePointItemUrl`. Shown only when there is no stored file. */
  readonly legacyUrl: string | null;
  /** What the link says, e.g. "Open file" or "Open filed signed offer". */
  readonly label: string;
  /**
   * What to say when the viewer MAY read files but this one is not in their list.
   *
   * The default is the withheld-file wording, which is right where the file was committed long ago
   * and the API left it out deliberately (restricted, corrected away). It is wrong immediately
   * after a record is written, where the far likelier answer is that the `store-file` effect has
   * not been delivered yet — and telling HR they lack permission for their own delivery lag is the
   * exact misdiagnosis this component avoids one branch above.
   */
  readonly whenMissing?: string;
}

/**
 * Where the checklist or a step shows its evidence: the stored file, opened in the in-app
 * viewer. The file is looked up in the person's list, so a file the API left out for this
 * viewer (restricted, corrected away) is never offered. A legacy SharePoint link is labelled
 * as one and never presented as the stored file.
 */
export function StoredFileLink({ personId, storedFileId, legacyUrl, label, whenMissing }: StoredFileLinkProps) {
  if (storedFileId !== null) return <StoredLink personId={personId} fileId={storedFileId} label={label} whenMissing={whenMissing} />;
  if (legacyUrl !== null) {
    return <Link href={legacyUrl} target="_blank" rel="noopener noreferrer">{`${label} (${LEGACY_LINK_LABEL})`}</Link>;
  }
  return null;
}

function StoredLink({ personId, fileId, label, whenMissing }: { readonly personId: string; readonly fileId: string; readonly label: string; readonly whenMissing?: string }) {
  const mayRead = useMayReadFiles();
  const files = usePersonFiles(personId, mayRead);
  const [open, setOpen] = useState(false);
  // Say so without asking. This link renders from History and from the Hiring section, neither of
  // which gates on the capability, so without this a viewer's page requested a list the API refuses
  // and logged an error before showing them a sentence anyway.
  //
  // DIFFERENT WORDS FROM THE `item === null` BRANCH BELOW, deliberately. Both said "File not
  // available to you", which meant this branch satisfied the one existing test that pins the other:
  // `does not offer a file the API left out for this viewer` found its expected sentence on the
  // first render, before auth resolved, and never reached the branch it was written for. That branch
  // is the only guard in the suite that a file the API deliberately withheld — restricted, corrected
  // away — is never offered as an openable link, and sharing copy took its teeth out. This wording
  // is the one `TimelinePanel` already shows for a withheld file.
  if (!mayRead) return <Caption1>File available to HR admins</Caption1>;
  if (files.isPending) return null;
  const item = files.data?.files.find((f) => f.fileId === fileId) ?? null;
  if (item === null) return <Caption1>{whenMissing ?? 'File not available to you'}</Caption1>;
  return (
    <>
      {/* A button styled as a link: it opens a dialog, and never navigates. */}
      <Link as="button" onClick={() => setOpen(true)}>{label}</Link>
      <FileViewer personId={personId} item={open ? item : null} onClose={() => setOpen(false)} />
    </>
  );
}
